import {
  Table,
  TableBody,
  TableContainer,
  makeStyles,
} from '@material-ui/core';
import {
  ColumnSizingInfoState,
  ColumnSizingState,
  OnChangeFn,
  SortingState,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import {
  ChangeEvent,
  MouseEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getOnHoverScrollbarStyle } from '../../styles/onHoverScrollbar';
import { useResponsiveMobile } from '../../hooks';
import { WbNoData } from '../WbNoData';
import { TableContent } from './contents/TableContent';
import {
  WbTableContextProvider,
  useWbTableContext,
} from './context/WbTableContext';
import { TableContentLoader } from './loaders/TableContentLoader';
import {
  CustomColumnDef,
  TableCellProps,
  WbColumnWidthType,
  WbTableLoader,
  WbTableProps,
} from './types';
import { TableHeader } from './contents/TableHeader';
import { WbTablePagination } from '../WbTablePagination';
import {
  DETAIL_EXPANDER_COLUMN_WIDTH,
  DETAIL_EXPANDER_ID,
  SELECTOR_COLUMN_WIDTH,
  SELECTOR_ID,
} from './constants';
import { ACTIONS_COLUMN_ID } from './StandardColumns';
import { getColumnId } from './utils/utils';
import { adaptToTanstackColumn } from './utils/tanstackTable';
import {
  computeFixedColumnWidths,
  expandCompactSizingConfigId,
  getColMaxWidthVarNameFromId,
  getColMinWidthVarNameFromId,
  getColWidthVarNameFromId,
  getCompactSizingConfigId,
  measureTableColumns,
} from './utils/columnResizingUtils';

const useStyles = makeStyles(
  theme => ({
    tableBase: {
      borderCollapse: 'separate',
      borderSpacing: 0,
    },
    layoutFixed: {
      tableLayout: 'fixed',
    },
    layoutAuto: {
      tableLayout: 'auto',
      minWidth: '100%',
    },
    paginationRoot: {
      position: 'relative',
      width: '100%',
    },
    paginationActions: {
      position: 'absolute',
      right: 0,
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
    },
    scrollbar: {
      ...getOnHoverScrollbarStyle(theme, false),
    },
  }),
  { name: 'WbTable' },
);

const getContainerAvailableWidth = (tableEl: HTMLElement): number => {
  const width = tableEl.getBoundingClientRect().width;

  return width;
};

function TableLoader({ loader, columnCount, rowCount = 10 }: WbTableLoader) {
  if (loader) {
    return loader;
  }
  if (columnCount) {
    return <TableContentLoader rows={rowCount} tableCells={columnCount} />;
  }
  return <></>;
}

function WbTableHeader<T>({
  styles,
  classNames,
}: Omit<WbTableProps<T>, 'pagination'>) {
  const { components, columns } = useWbTableContext<T>();
  if (components?.tableHeader) {
    return components.tableHeader;
  }

  if (columns.length) {
    return (
      <TableHeader
        className={classNames?.header}
        style={{ ...styles?.header }}
      />
    );
  }
  return <></>;
}

const DEFAULT_COLUMN_SIZING_INFO = {
  startOffset: null,
  startSize: null,
  deltaOffset: null,
  deltaPercentage: null,
  isResizingColumn: false as const,
  columnSizingStart: [],
};

function WbTableContent<T>({ styles, classNames }: WbTableProps<T>) {
  const { columns, rows, components, disableRowHighlightOnHover } =
    useWbTableContext<T>();
  if (components?.tableContent?.body) {
    return components.tableContent.body;
  }
  if (columns.length && rows.length) {
    return (
      <TableContent<T>
        disableRowHighlightOnHover={disableRowHighlightOnHover}
        rowClassName={classNames?.body}
        rowStyle={styles?.body}
      />
    );
  }
  return <></>;
}

export function WbTable<T>(props: WbTableProps<T>) {
  const {
    components,
    noDataLabel,
    noDataComponent,
    pagination,
    styles,
    classNames,
    stickyHeader,
    selectedItems,
    selection,
    selectionDisabled,
    setSelectedItems,
    checkIfDisabled,
    tooltipIfDisabled,
    tableStyles,
    expandedRowDetail,
    onExpandedRowDetailChange,
    sorting: propsSorting,
    onSortingChange: propsOnSortingChange,
    manualSorting,
    columnPinning: propsColumnPinning,
    enableAutoLayout,
    renderRowDetail,
    pinActionsColumnOnMobile = true,
  } = props;
  const classes = useStyles();
  const isMobile = useResponsiveMobile();

  const columns = components?.tableContent?.columns;

  const handleChangePage = (
    _event: MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    pagination?.onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (
    event: ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    pagination?.onRowsPerPageChange(parseInt(String(event.target.value), 10));
  };

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<SortingState>(propsSorting || []);

  const [columnSizingState, setColumnSizingState] = useState<ColumnSizingState>(
    {},
  );

  const [columnSizingInfoState, setColumnSizingInfoState] =
    useState<ColumnSizingInfoState>(DEFAULT_COLUMN_SIZING_INFO);

  // keeps track of what columns the user manually resized
  const resizedColumnsIdsRef = useRef(new Set<string>());

  const isColumnResized = useCallback((colId: string) => {
    return resizedColumnsIdsRef.current.has(colId);
  }, []);

  const markColumnAsResized = useCallback((colId: string) => {
    resizedColumnsIdsRef.current.add(colId);
  }, []);

  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const actualColumns = useMemo<Array<TableCellProps<T>> | undefined>(() => {
    const privateColumns: Array<TableCellProps<T>> = [];
    // add expander column if detail expansion is enabled
    if (renderRowDetail && columns)
      privateColumns.push({
        uniqueFieldId: DETAIL_EXPANDER_ID,
        width: DETAIL_EXPANDER_COLUMN_WIDTH,
      });

    // add selector column if selection is enabled
    if (selection && columns)
      privateColumns.push({
        uniqueFieldId: SELECTOR_ID,
        width: SELECTOR_COLUMN_WIDTH,
      });

    return [...privateColumns, ...(columns ?? [])];
  }, [columns, selection, renderRowDetail]);

  // If some columns use auto widths, we need to switch to auto layout (not enabled by default because it has some gotchas)
  const autoLayout =
    (actualColumns?.some(c => c.width === 'auto') && enableAutoLayout) ?? false;

  // our columns adapated to tanstack API
  const tanstackColumns: CustomColumnDef<T>[] = useMemo(() => {
    if (!actualColumns) return [];
    return actualColumns.map(c => adaptToTanstackColumn(c));
  }, [actualColumns]);

  // Check if we should pin the actions column
  const shouldPinActionsColumn = useMemo(() => {
    if (!isMobile || !actualColumns || !pinActionsColumnOnMobile) return false;
    const lastColumn = actualColumns[actualColumns.length - 1];

    if (!lastColumn) return false;
    return getColumnId(lastColumn) === ACTIONS_COLUMN_ID;
  }, [isMobile, actualColumns, pinActionsColumnOnMobile]);

  // Configure column pinning state
  const columnPinning = useMemo(() => {
    const pinning: { left?: string[]; right?: string[] } = {
      left: propsColumnPinning?.left,
      right: propsColumnPinning?.right,
    };
    if (shouldPinActionsColumn && tanstackColumns?.length) {
      const lastColumnId = tanstackColumns[tanstackColumns.length - 1]?.id;
      if (lastColumnId) {
        pinning.right = [...(pinning.right || []), lastColumnId];
      }
    }
    return pinning;
  }, [shouldPinActionsColumn, tanstackColumns, propsColumnPinning]);

  const onColumnsSizingInfoChange = useCallback<
    OnChangeFn<ColumnSizingInfoState>
  >(
    updater => {
      setColumnSizingInfoState(old => {
        let newVal = updater instanceof Function ? updater(old) : updater;

        const resizedColumnId = newVal.isResizingColumn;

        if (resizedColumnId && !isColumnResized(resizedColumnId)) {
          // we keep track of what columns have been resized manually by the user to keep them fixed
          markColumnAsResized(resizedColumnId);
          if (!autoLayout) return newVal;
          // when resizing the column for the first time in autoLayout,
          // the size in the state will be off sync,
          // so we measure the current column width first to "fix" it in sizing state
          const tableEl = tableContainerRef.current?.querySelector('table');
          if (!tableEl) throw new Error('Could not measure table');
          const colCurrWidth = measureTableColumns(tableEl)[resizedColumnId];
          newVal = {
            ...newVal,
            columnSizingStart: [[resizedColumnId, colCurrWidth]],
            startSize: colCurrWidth,
          };

          setColumnSizingState(oldSizing => ({
            ...oldSizing,
            [resizedColumnId]: colCurrWidth,
          }));
        }
        return newVal;
      });
    },
    [
      setColumnSizingInfoState,
      setColumnSizingState,
      autoLayout,
      isColumnResized,
      markColumnAsResized,
    ],
  );
  // define stable data fallback for Tanstack Table: https://tanstack.com/table/latest/docs/guide/data#give-data-a-stable-reference
  const fallbackData: T[] = useMemo(() => [], []);

  const table = useReactTable({
    data: components?.tableContent?.rows || fallbackData,
    columns: tanstackColumns,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: propsOnSortingChange ?? setSorting,
    onColumnSizingChange: setColumnSizingState,
    onColumnSizingInfoChange: onColumnsSizingInfoChange,
    ...(onExpandedRowDetailChange
      ? { onExpandedChange: onExpandedRowDetailChange }
      : {}),
    getRowId: components?.tableContent?.getRowId,
    columnResizeMode: 'onChange',
    state: {
      ...(expandedRowDetail ? { expanded: expandedRowDetail } : {}),
      sorting: propsSorting ?? sorting,
      columnPinning: columnPinning,
      columnSizing: columnSizingState,
      columnSizingInfo: columnSizingInfoState,
    },
    manualSorting: manualSorting,
  });

  const rightPinnedColumns = table.getState().columnPinning.right;

  // Calculate last non-pinned column index and reference
  const lastNonPinnedData = useMemo(() => {
    const usedColumns = actualColumns || [];
    const rightPinned = new Set(rightPinnedColumns || []);
    const lastNonPinnedIndex = (usedColumns ?? []).reduce(
      (lastIndex, c, idx) => {
        const colId = getColumnId(c);

        return !rightPinned.has(colId) ? idx : lastIndex;
      },
      -1,
    );

    let lastNonPinnedColumn = null;
    if (lastNonPinnedIndex !== -1) {
      const cell = usedColumns[lastNonPinnedIndex];
      const colId = getColumnId(cell);
      lastNonPinnedColumn = table.getColumn(colId);
    }

    return { lastNonPinnedIndex, lastNonPinnedColumn };
  }, [actualColumns, table, rightPinnedColumns]);

  // a string id representing the sizing configuration in the given columns, columns reference may change as it is an array,
  // but this remains stable as long as the sizing configuration is the same
  const columnSizingConfigId = useMemo(
    () => getCompactSizingConfigId(tanstackColumns ?? []),
    [tanstackColumns],
  );

  const { setColumnSizing } = table;

  const noData =
    !components?.tableLoader?.loading &&
    !components?.tableContent?.body &&
    !components?.tableContent?.rows?.length;

  /**
   * When in fixed layout and we are loading the table for the first time or the available space or the column width config changes, recompute the widths for all columns,
   * distributing equally the remaining free space between the columns with no defined width
   */
  const updateFixedColumnSizing = useCallback(
    (tableContainerEl: HTMLDivElement | null | undefined): void => {
      if (!tableContainerEl) return;

      const tableEl = tableContainerEl?.querySelector('table');
      if (!tableEl) throw new Error('Could not measure table');

      const columnSizingConfig =
        expandCompactSizingConfigId(columnSizingConfigId);
      const tableWidth = getContainerAvailableWidth(tableContainerEl);

      setContainerWidth(tableWidth);

      setColumnSizing(old => {
        const widthsMap = computeFixedColumnWidths(columnSizingConfig, {
          tableWidth,
          resizingState: old,
          resizedColumns: resizedColumnsIdsRef.current,
        });
        return {
          ...old,
          ...widthsMap,
        };
      });
    },
    [columnSizingConfigId, setColumnSizing],
  );

  useLayoutEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer || autoLayout || noData) return () => {};

    updateFixedColumnSizing(tableContainer);

    let prevWidth: number;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const width = entry.borderBoxSize?.[0].inlineSize;

        if (width !== prevWidth) {
          prevWidth = width;
          updateFixedColumnSizing(tableContainer);
        }
      }
    });

    if (tableContainer) {
      resizeObserver.observe(tableContainer);
    }

    return () => {
      if (tableContainer && resizeObserver) {
        resizeObserver.unobserve(tableContainer);
      }
    };
  }, [updateFixedColumnSizing, autoLayout, noData]);

  const columnSizing = table.getState().columnSizing;

  // compute the actual css width values to pass to the cells of the table
  const columnSizeVars = useMemo(() => {
    let remainingWidth = containerWidth || 0;
    return (tanstackColumns ?? []).reduce<{ [key: string]: number | string }>(
      (acc, column, index) => {
        const colId = column.id;

        // we rely on tanstack column for resize functionality
        const tanstackCol = table.getColumn(colId);

        // should not happen
        if (!tanstackCol) return acc;

        const isLastNonPinned = index === lastNonPinnedData.lastNonPinnedIndex;

        const hasBeenResized = isColumnResized(colId);

        const computeWidths = () => {
          // when some column is defined as auto, compute sizes as auto layout
          if (autoLayout) {
            const parsedWidth = column.parsedWidth;
            let width: string = 'auto';
            let minWidth: string = `${column.parsedMinWidth}px`;
            let maxWidth: string = `${column.parsedMaxWidth}px`;
            switch (parsedWidth.type) {
              case WbColumnWidthType.FIXED:
                width = minWidth = maxWidth = `${parsedWidth.value}px`;
                break;
              case WbColumnWidthType.PERCENT:
                width = `${parsedWidth.value}%`;
                break;
              case WbColumnWidthType.AUTO: {
                const autoMinWidth = column.parsedAutoMinWidth;
                const autoMaxWidth = column.parsedAutoMaxWidth;
                // by setting the width to 1px the column will still fit content, but not grow based on available space like "auto" does
                width = '1px';
                minWidth = autoMinWidth ? `${autoMinWidth}px` : 'auto';
                if (autoMaxWidth) maxWidth = `${autoMaxWidth}px`;
                break;
              }
              case WbColumnWidthType.NOT_DEFINED:
              default:
                width = 'auto';
            }
            // in case the column has been resized, the fixed size set by the user will override all and make the column exactly as wide
            if (hasBeenResized) {
              width = minWidth = maxWidth = `${tanstackCol.getSize()}px`;
            }
            // last column will fill all available space
            if (isLastNonPinned) width = maxWidth = 'auto';

            return { width, minWidth, maxWidth };
          }

          // handle fixed layout mode
          let tmpWidth: number = tanstackCol.getSize();

          // for last non-pinned column, fill remaining space
          if (isLastNonPinned) {
            tmpWidth = Math.max(remainingWidth, tmpWidth);
          }

          remainingWidth -= tmpWidth;

          const width = `${tmpWidth}px`;

          return { width, minWidth: width, maxWidth: width };
        };

        const { width, minWidth, maxWidth } = computeWidths();

        // Assign the calculated size to CSS variables, to optimize performance and change column width without re-rendering individual cells
        acc[`${getColWidthVarNameFromId(colId)}`] = width;
        acc[`${getColMinWidthVarNameFromId(colId)}`] = minWidth;
        acc[`${getColMaxWidthVarNameFromId(colId)}`] = maxWidth;

        return acc;
      },
      {},
    );
    // we need to listen to columnSizing changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tanstackColumns,
    table,
    containerWidth,
    columnSizing,
    lastNonPinnedData.lastNonPinnedIndex,
    autoLayout,
    isColumnResized,
  ]);

  let columnCountTotal = components?.tableContent?.columns?.length;
  // add selector column to column count if defined
  if (columnCountTotal && selection) columnCountTotal++;

  if (noData) {
    return noDataComponent || <WbNoData fillContainer text={noDataLabel} />;
  }

  return (
    <>
      <WbTableContextProvider
        renderRowDetail={renderRowDetail}
        columns={actualColumns}
        tanstackTableInstance={table}
        components={components}
        selection={selection}
        selectionDisabled={selectionDisabled}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        checkIfDisabled={checkIfDisabled}
        tooltipIfDisabled={tooltipIfDisabled}
      >
        <TableContainer
          className={clsx(classes.scrollbar, classNames?.container)}
          style={{
            ...styles?.container,
            overflowX: 'auto',
          }}
        >
          {/* This div is used to measure the actual table available space, without padding and scrollbar from parent */}
          <div ref={tableContainerRef}>
            <Table
              stickyHeader={stickyHeader}
              size="small"
              className={clsx(
                classes.tableBase,
                autoLayout && classes.layoutAuto,
                !autoLayout && classes.layoutFixed,
              )}
              style={{
                ...columnSizeVars,
                ...tableStyles,
              }}
            >
              <WbTableHeader styles={styles} classNames={classNames} />
              {components?.tableLoader?.loading ? (
                <TableBody>
                  <TableLoader
                    columnCount={columnCountTotal}
                    {...components?.tableLoader}
                  />
                </TableBody>
              ) : (
                <WbTableContent styles={styles} classNames={classNames} />
              )}
            </Table>
          </div>
        </TableContainer>
        {!!pagination?.count && (
          <WbTablePagination
            rowsPerPageOptions={
              pagination.rowsPerPageOptions || [25, 50, 75, 100, 200]
            }
            component="div"
            count={pagination.count}
            rowsPerPage={pagination.limit}
            labelDisplayedRows={
              pagination.countlessOptions
                ? ({ from, to }) => `${from}-${to}`
                : undefined
            }
            nextIconButtonProps={
              pagination.countlessOptions
                ? {
                    disabled: !pagination.countlessOptions.hasNextPage(),
                  }
                : undefined
            }
            backIconButtonProps={
              pagination.countlessOptions
                ? {
                    disabled: pagination.countlessOptions.isFirstPage(),
                  }
                : undefined
            }
            page={
              'currentPage' in pagination
                ? pagination.currentPage
                : Math.floor(pagination.offset / pagination.limit)
            }
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            style={{ alignSelf: 'flex-end', flexShrink: 0 }}
          />
        )}
      </WbTableContextProvider>
    </>
  );
}
